import type { ReactNode } from 'react';
import type { ApplicationManifest, ApplicationProps } from '@/applications/types';
import { DEFAULT_CAPABILITIES } from '@/applications/types';
import { asApplicationId, isContainer, type ObjectId, type SpatialObject } from '@/types';
import styles from './FilesApp.module.css';

const formatBytes = (n: number): string =>
  n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(1)} MB`;

/**
 * Browses the workspace's files and folders as a tree. Selecting an entry selects
 * the object in the world; double-click centres on a file or collapses a folder.
 * Reads everything through the application API; its only own state is which
 * folders are collapsed.
 */
export const FilesApp = ({ api, state, setState }: ApplicationProps) => {
  const objects = api.listObjects().filter((o) => o.kind === 'file' || o.kind === 'folder');
  const collapsed = new Set(typeof state.collapsed === 'string' && state.collapsed ? state.collapsed.split(',') : []);
  const byParent = new Map<ObjectId | null, SpatialObject[]>();
  for (const o of objects) {
    const parent = o.parentId !== null && objects.some((p) => p.id === o.parentId) ? o.parentId : null;
    byParent.set(parent, [...(byParent.get(parent) ?? []), o]);
  }
  const toggle = (id: ObjectId) => {
    const next = new Set(collapsed);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setState({ ...state, collapsed: [...next].join(',') });
  };
  const selected = new Set(api.selectedIds);

  const renderLevel = (parent: ObjectId | null, depth: number): ReactNode =>
    (byParent.get(parent) ?? [])
      .sort((a, b) => Number(!isContainer(a)) - Number(!isContainer(b)) || a.name.localeCompare(b.name))
      .map((o) => (
        <li key={o.id}>
          <button
            type="button"
            className={styles.row}
            data-selected={selected.has(o.id) || undefined}
            data-testid="files-entry"
            style={{ paddingLeft: 10 + depth * 16 }}
            onClick={() => {
              api.selectObjects([o.id]);
            }}
            onDoubleClick={() => {
              if (isContainer(o)) toggle(o.id);
              else api.centerOn(o.id);
            }}
          >
            <span className={styles.icon} aria-hidden="true">
              {isContainer(o) ? (collapsed.has(o.id) ? '▸' : '▾') : '▢'}
            </span>
            <span className={styles.name}>{o.kind === 'file' ? o.fileName : o.name}</span>
            <span className={styles.meta}>
              {o.kind === 'file' ? formatBytes(o.byteSize) : `${(byParent.get(o.id) ?? []).length} items`}
            </span>
          </button>
          {isContainer(o) && !collapsed.has(o.id) && <ul className={styles.list}>{renderLevel(o.id, depth + 1)}</ul>}
        </li>
      ));

  return (
    <div className={styles.root} data-scrollable>
      {objects.length === 0 ? (
        <p className={styles.empty}>No files or folders in this workspace yet. Add some from the toolbar.</p>
      ) : (
        <ul className={styles.list}>{renderLevel(null, 0)}</ul>
      )}
    </div>
  );
};

export const filesManifest: ApplicationManifest = {
  id: asApplicationId('app.files'),
  name: 'Files',
  version: '1.0.0',
  icon: '▤',
  category: 'files',
  description: 'Browse the files and folders placed in this workspace.',
  defaultSize: { width: 360, height: 420 },
  defaultPosition: 'cascade',
  capabilities: { ...DEFAULT_CAPABILITIES, multiInstance: false, observesWorkspace: true },
  component: FilesApp,
  normalizeState: (state) => ({ collapsed: typeof state.collapsed === 'string' ? state.collapsed : '' }),
};
